import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, Upload, Users, Trophy, RefreshCw, FileText, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UploadRow({ label, endpoint, method = "POST", description, sampleHeaders, token, onDone }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [last, setLast] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setLast(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}${endpoint}`, fd, { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data || {};
      const summary = `Inserted ${d.inserted ?? 0}${d.skipped?.length ? ` · Skipped ${d.skipped.length}` : ""}`;
      setLast({ ok: true, summary, raw: d });
      if (d.unknown_teams?.length) {
        toast.warning(`${summary}. Unknown teams: ${d.unknown_teams.join(", ")}`);
      } else {
        toast.success(summary);
      }
      onDone?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Upload failed";
      setLast({ ok: false, summary: msg });
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col gap-2" data-testid={`upload-row-${endpoint.replace(/\W+/g, "-")}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <p className="text-sm font-bold text-slate-800">{label}</p>
          <p className="text-[11px] text-muted-foreground">{description}</p>
          {sampleHeaders && (
            <p className="text-[10px] font-mono text-slate-500 mt-0.5">CSV: {sampleHeaders}</p>
          )}
          {last && (
            <p className={`text-[11px] mt-1 ${last.ok ? "text-emerald-700" : "text-rose-600"}`}>
              {last.summary}
            </p>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`upload-input${endpoint.replace(/\W+/g, "-")}`}
        />
        <Button
          size="sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="bg-rose-600 hover:bg-rose-700 text-white text-xs"
          data-testid={`upload-btn${endpoint.replace(/\W+/g, "-")}`}
        >
          {uploading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
          {uploading ? "Uploading..." : "Choose CSV"}
        </Button>
      </div>
    </div>
  );
}

export default function AdminIntramuralUploadsView({ token, onBack }) {
  const [marinerBoys, setMarinerBoys] = useState({ assistants: 0, heads: 0 });
  const [marinerGirls, setMarinerGirls] = useState({ assistants: 0, heads: 0 });
  const [navCounselors, setNavCounselors] = useState({ count: 0 });

  const headers = { Authorization: `Bearer ${token}` };
  const refresh = useCallback(async () => {
    try {
      const [bA, bH, gA, gH, navC] = await Promise.all([
        axios.get(`${API}/intramurals/boys/assistants`, { headers }),
        axios.get(`${API}/intramurals/boys/heads`, { headers }),
        axios.get(`${API}/intramurals/girls/assistants`, { headers }),
        axios.get(`${API}/intramurals/girls/heads`, { headers }),
        axios.get(`${API}/nav-intramurals/counselors`, { headers }),
      ]);
      setMarinerBoys({ assistants: bA.data?.assistants?.length || 0, heads: bH.data?.heads?.length || 0 });
      setMarinerGirls({ assistants: gA.data?.assistants?.length || 0, heads: gH.data?.heads?.length || 0 });
      setNavCounselors({ count: navC.data?.counselors?.length || 0 });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-4" data-testid="admin-intramural-uploads-view">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="px-2" data-testid="intramural-uploads-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h2 className="font-heading font-bold text-lg flex items-center gap-2">
          <Trophy className="w-5 h-5 text-rose-600" /> Intramural Counselor Uploads
        </h2>
        <Button variant="outline" size="sm" onClick={refresh} className="ml-auto" data-testid="intramural-uploads-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 flex gap-2">
        <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Upload counselor → team CSVs without logging in as a supervisor. Each upload <strong>replaces</strong>
          all prior assignments for that division. Team names must match exactly — typos will be reported as
          "unknown teams" in the toast.
        </span>
      </div>

      {/* ── Mariner Boys ── */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-bold text-slate-800">Mariner Intramurals — Boys</h3>
            <span className="ml-auto text-[10px] text-slate-500">
              {marinerBoys.assistants} assistants · {marinerBoys.heads} heads currently assigned
            </span>
          </div>
          <UploadRow
            label="Boys → Assistants"
            description="Assistant counselors paired with each Mariner boys' team."
            sampleHeaders="last_name, first_name, team"
            endpoint="/intramurals/boys/assistants/upload"
            token={token}
            onDone={refresh}
          />
          <UploadRow
            label="Boys → Heads"
            description="Heads (station leads) per activity / game."
            sampleHeaders="last_name, first_name, activity"
            endpoint="/intramurals/boys/heads/upload"
            token={token}
            onDone={refresh}
          />
        </CardContent>
      </Card>

      {/* ── Mariner Girls ── */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-pink-600" />
            <h3 className="text-sm font-bold text-slate-800">Mariner Intramurals — Girls</h3>
            <span className="ml-auto text-[10px] text-slate-500">
              {marinerGirls.assistants} assistants · {marinerGirls.heads} heads currently assigned
            </span>
          </div>
          <UploadRow
            label="Girls → Assistants"
            description="Assistant counselors paired with each Mariner girls' team."
            sampleHeaders="last_name, first_name, team"
            endpoint="/intramurals/girls/assistants/upload"
            token={token}
            onDone={refresh}
          />
          <UploadRow
            label="Girls → Heads"
            description="Heads (station leads) per activity / game."
            sampleHeaders="last_name, first_name, activity"
            endpoint="/intramurals/girls/heads/upload"
            token={token}
            onDone={refresh}
          />
        </CardContent>
      </Card>

      {/* ── Nav Intramurals ── */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-bold text-slate-800">Navigator Intramurals — Counselors</h3>
            <span className="ml-auto text-[10px] text-slate-500">
              {navCounselors.count} counselors currently assigned
            </span>
          </div>
          <UploadRow
            label="Nav → Counselors per Team"
            description="Counselors assigned to each Nav intramural team. Names appear at the top of each team card on the Navigator Intramurals dashboard."
            sampleHeaders="last_name, first_name, team"
            endpoint="/nav-intramurals/counselors/upload"
            token={token}
            onDone={refresh}
          />
        </CardContent>
      </Card>
    </div>
  );
}
